import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import { userPreferenceClient } from 'clients';
import { UserPreference } from 'types/models/user-preference';
import { APIResponseParsed } from 'types/response';

import {
  editMyPreference,
  editMyPreferenceFailure,
  editMyPreferenceSuccess,
  fetchMyPreferences,
  fetchMyPreferencesFailure,
  fetchMyPreferencesSuccess,
  setMyPreferences
} from './actions';
import ActionTypes from './constants';

// ==============================
// WORKER SAGAS
// ==============================
export function* requestFetchMyPreferences(
  action: ReturnType<typeof fetchMyPreferences>
): SagaIterator {
  const { payload: { entity } } = action;

  const response: APIResponseParsed<UserPreference[]> = yield call(
    userPreferenceClient.fetchPreferences,
    entity ?? {}
  );
  if (response.data) {
    yield all([
      put(fetchMyPreferencesSuccess()),
      put(setMyPreferences(response.data))
    ]);
  } else {
    const { entityKind, entityId } = entity ?? {};
    const message = response.error.message
      || (entityKind
        ? `Failed to fetch preference(s) for ${entityKind} ${entityId ?? ''}`.trim()
        : 'Failed to fetch preferences');
    yield put(fetchMyPreferencesFailure(message));
  }
}

export function* requestEditMyPreference(
  action: ReturnType<typeof editMyPreference>
): SagaIterator {
  const { payload: updateDTO } = action;

  const response: APIResponseParsed<UserPreference> = yield call(
    userPreferenceClient.editPreference,
    updateDTO
  );
  if (response.data) {
    yield all([
      put(editMyPreferenceSuccess()),
      put(setMyPreferences([response.data]))
    ]);
  } else {
    const message = response.error.message
      || `Failed to update preference for ${updateDTO.entityKind} ${updateDTO.entityId}`;
    yield put(editMyPreferenceFailure(message));
  }
}

// ==============================
// WATCHER SAGAS
// ==============================
export function* watchFetchMyPreferencesRequest() {
  yield takeLatest(ActionTypes.FETCH_MY_PREFERENCES_REQUEST, requestFetchMyPreferences);
}

export function* watchEditMyPreferenceRequest() {
  yield takeLatest(ActionTypes.EDIT_MY_PREFERENCE_REQUEST, requestEditMyPreference);
}

// ==============================
// EXPORTS
// ==============================
export default function* myPreferencesSaga() {
  yield all([
    fork(watchFetchMyPreferencesRequest),
    fork(watchEditMyPreferenceRequest)
  ]);
}
